/**
 * @graphql cc
 */
import React, { useCallback, useRef, useState, useEffect } from 'react';

import { token } from '@atlaskit/tokens';
import { cssMap, cx } from '@atlaskit/css';
import { Flex, Box } from '@atlaskit/primitives/compiled';

import {
	useObjectCommentDispatchContext,
	useObjectCommentContext,
} from '@confluence/comment-context/entry-points/ObjectCommentContext';
import { useSessionData } from '@confluence/session-data';
import { fg } from '@confluence/feature-gating';
import {
	ADD_PAGE_COMMENT_EXPERIENCE,
	ADD_PAGE_COMMENT_LOAD_EXPERIENCE,
	ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
	VIEW_PAGE_LABELS_IN_FOOTER_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceStart } from '@confluence/experience-tracker/entry-points/component';
import { startEditorExperiences } from '@confluence/experience-tracker/entry-points/startExperienceForRoute';
import { usePageContentId } from '@confluence/page-context';
import { AnalyticsSource } from '@confluence/comments-util/entry-points/analytics';
import { LoadablePaint } from '@confluence/loadable';
import { componentWithFG } from '@confluence/feature-gate-component';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary';

import { useDraftCommentBox } from '../hooks/useDraftCommentBox';

import { ObjectAddCommentPrompt } from './ObjectAddCommentPrompt';
import { ObjectCommentPageReactions } from './ObjectCommentPageReactions';
import { DraftCommentBox } from './DraftCommentBox';
import { ObjectAddCommentEditor } from './ObjectAddCommentEditor';
import { ObjectAddCommentProfileWrapper } from './ObjectAddCommentProfileWrapper';
import { ObjectAddCommentContainerLegacy } from './ObjectAddCommentContainerLegacy';

const FooterLabelsLoader = LoadablePaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-FooterLabels" */ '@confluence/labels-footer'))
			.FooterLabels,
});

const styles = cssMap({
	oldAddCommentContainerStyles: {
		position: 'sticky',
		bottom: token('space.0'),
		display: 'flex',
		width: '100%',
		padding: token('space.200'),
		borderTop: `${token('border.width')} solid ${token('color.border')}`,
		backgroundColor: token('color.background.input'),
	},
	addCommentContainerStyles: {
		position: 'sticky',
		bottom: token('space.0'),
		display: 'flex',
		width: '100%',
		padding: token('space.200'),
		borderTop: `${token('border.width')} solid ${token('color.border')}`,
		backgroundColor: token('elevation.surface'),
	},
	addCommentClosedContainerStyles: {
		width: '100%',
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		flexShrink: 0,
	},
	hideStyle: {
		display: 'none',
	},
	addCommentReactionsContainerStyles: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
	},
});

export const ObjectAddCommentContainerComponent = ({
	showReactions,
	showLabels,
	isOffline,
	optimisticReactions = false,
}: {
	showReactions: boolean;
	showLabels: boolean;
	isOffline?: boolean;
	optimisticReactions?: boolean;
}) => {
	const [isEditorEmpty, setIsEditorEmpty] = useState<boolean>(true);

	const { edition } = useSessionData();
	const shouldAutofocusPromptRef = useRef<boolean>(false);
	const { expandCommentContainer } = useObjectCommentDispatchContext();

	const setAutoFocusPromptRef = useCallback((value: boolean) => {
		shouldAutofocusPromptRef.current = value;
	}, []);
	const { expanded } = useObjectCommentContext();

	// Use the custom hook for draft box logic
	const { shouldShowDraftBox, hideDraftBox } = useDraftCommentBox({
		expanded,
		isEditorEmpty,
	});

	// Monitor expanded state changes
	useEffect(() => {
		// Experience metric will be added here
	}, [expanded, shouldShowDraftBox]);

	const [contentId] = usePageContentId();

	const objectAddCommentContainerId = 'addCommentButton';

	const handleExpandCommentContainer = useCallback(() => {
		startEditorExperiences(
			{
				name: ADD_PAGE_COMMENT_EXPERIENCE,
				attributes: {
					editor: 'FABRIC',
					isSingleExperience: true,
					edition,
					source: AnalyticsSource.PAGE_FOOTER,
				},
			},
			{
				name: ADD_PAGE_COMMENT_LOAD_EXPERIENCE,
			},
			{
				name: ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
			},
			true,
		);

		expandCommentContainer();
	}, [edition, expandCommentContainer]);

	return (
		<>
			<Box
				id={objectAddCommentContainerId}
				xcss={cx(
					fg('confluence_frontend_content_wrapper') && styles.addCommentContainerStyles,
					!fg('confluence_frontend_content_wrapper') && styles.oldAddCommentContainerStyles,
				)}
				testId={objectAddCommentContainerId}
			>
				{!expanded ? (
					<Box xcss={styles.addCommentClosedContainerStyles}>
						<ObjectAddCommentPrompt
							onClick={handleExpandCommentContainer}
							autofocusRef={shouldAutofocusPromptRef}
						/>
						<Flex gap="space.050">
							{showLabels && fg('confluence_frontend_labels_in_footer') && (
								<>
									<ExperienceStart id={contentId} name={VIEW_PAGE_LABELS_IN_FOOTER_EXPERIENCE} />
									<FooterLabelsLoader />
								</>
							)}
							{showReactions && (
								<Box xcss={styles.addCommentReactionsContainerStyles}>
									<ObjectCommentPageReactions
										isOffline={isOffline}
										optimisticReactions={optimisticReactions}
									/>
								</Box>
							)}
						</Flex>
					</Box>
				) : (
					<ObjectAddCommentProfileWrapper>
						{expanded && shouldShowDraftBox && (
							<DraftCommentBox expandPageCommentEditorHandler={hideDraftBox} />
						)}
						<Box xcss={cx(shouldShowDraftBox && styles.hideStyle)}>
							<ObjectAddCommentEditor
								isOffline={isOffline}
								setAutoFocusPromptRef={setAutoFocusPromptRef}
								setIsEditorEmpty={setIsEditorEmpty}
								objectAddCommentContainerId={objectAddCommentContainerId}
							/>
						</Box>
					</ObjectAddCommentProfileWrapper>
				)}
			</Box>
		</>
	);
};

// This FG lets us quick revert without causing a HOT
// Don't update the legacy component, it will be removed shortly
export const ObjectAddCommentContainer = withErrorBoundary({
	attribution: Attribution.COMMENTS,
})(
	componentWithFG(
		'confluence_comment_object_add_comment_refactor',
		ObjectAddCommentContainerComponent,
		ObjectAddCommentContainerLegacy,
	),
);
